<template>
  <div class="cpk">
    <!-- Continent quick-select + search -->
    <div class="cpk__toolbar">
      <div class="cpk__continents">
        <button
          v-for="cont in continents"
          :key="cont.code"
          type="button"
          class="cpk__cont"
          :class="{ 'cpk__cont--full': isContinentFullySelected(cont.code) }"
          :title="continentTitle(cont)"
          @click="toggleContinent(cont.code)"
        >
          {{ $t(cont.label) }}
          <span class="cpk__cont-count">{{ selectedInContinent(cont.code) }}/{{ cont.total }}</span>
        </button>
      </div>
      <Input
        v-model="search"
        search
        clearable
        :placeholder="$t('Search a country')"
        class="cpk__search"
      />
    </div>

    <!-- Selection summary -->
    <div class="cpk__summary">
      <button
        type="button"
        class="cpk__selected-count"
        :class="{ 'cpk__selected-count--clickable': value.length }"
        :disabled="!value.length"
        :title="value.length ? $t('View selected countries') : ''"
        @click="openSelected"
      >
        <Icon type="md-checkmark-circle" />
        {{ value.length }} {{ $t("selected") }}
        <Icon
          v-if="value.length"
          type="ios-eye"
          class="cpk__selected-eye"
        />
      </button>
      <button
        v-if="value.length"
        type="button"
        class="cpk__clear"
        @click="clearAll"
      >
        {{ $t("Clear all") }}
      </button>
    </div>

    <!-- Selected countries modal -->
    <Modal
      v-model="showSelected"
      :title="$t('Selected countries') + ' (' + value.length + ')'"
      width="560"
      footer-hide
    >
      <div class="cpk__sel-modal">
        <Input
          v-model="selSearch"
          search
          clearable
          :placeholder="$t('Search a country')"
          class="cpk__sel-search"
        />
        <div
          v-for="c in filteredSelectedCountries"
          :key="c.iso"
          class="cpk__sel-item"
        >
          <img
            v-if="c.flag"
            :src="c.flag"
            class="cpk__flag"
            :alt="c.display_name"
          >
          <span class="cpk__sel-name">{{ c.display_name }}</span>
          <button
            type="button"
            class="cpk__sel-remove"
            :title="$t('Remove')"
            @click="toggleCountry(c.iso)"
          >
            <Icon type="md-close" />
          </button>
        </div>
        <div
          v-if="!filteredSelectedCountries.length"
          class="cpk__empty"
        >
          {{ selectedCountries.length ? $t("No country found") : $t("No country selected") }}
        </div>
      </div>
    </Modal>

    <!-- Country grid with flags -->
    <div class="cpk__grid">
      <label
        v-for="c in visibleCountries"
        :key="c.iso"
        class="cpk__country"
        :class="{ 'cpk__country--on': isSelected(c.iso) }"
      >
        <input
          type="checkbox"
          class="cpk__cb"
          :checked="isSelected(c.iso)"
          @change="toggleCountry(c.iso)"
        >
        <img
          v-if="c.flag"
          :src="c.flag"
          class="cpk__flag"
          :alt="c.display_name"
        >
        <span class="cpk__cname">{{ c.display_name }}</span>
      </label>
      <div
        v-if="!visibleCountries.length"
        class="cpk__empty"
      >
        {{ $t("No country found") }}
      </div>
    </div>
  </div>
</template>

<script>
// Continent codes as returned by the API (countries_plus).
const CONTINENTS = [
  { code: "AF", label: "Africa" },
  { code: "EU", label: "Europe" },
  { code: "AS", label: "Asia" },
  { code: "NA", label: "North America" },
  { code: "SA", label: "South America" },
  { code: "OC", label: "Oceania" },
];

export default {
  name: "CountryPicker",
  props: {
    // v-model: array of selected ISO codes.
    value: { type: Array, default: () => [] },
  },
  data() {
    return { search: "", showSelected: false, selSearch: "" };
  },
  computed: {
    countries() {
      return this.worldInfoStore.countryList || [];
    },
    continents() {
      // Only show continents that actually have countries, with their totals.
      const counts = {};
      this.countries.forEach((c) => {
        counts[c.continent] = (counts[c.continent] || 0) + 1;
      });
      return CONTINENTS.filter((c) => counts[c.code]).map((c) => ({
        ...c,
        total: counts[c.code],
      }));
    },
    visibleCountries() {
      const q = this.search.trim().toLowerCase();
      let list = this.countries;
      if (q) {
        list = list.filter(
          (c) =>
            (c.display_name || "").toLowerCase().includes(q) ||
            (c.iso || "").toLowerCase().includes(q)
        );
      }
      return [...list].sort((a, b) =>
        (a.display_name || "").localeCompare(b.display_name || "")
      );
    },
    selectedSet() {
      return new Set(this.value);
    },
    selectedCountries() {
      // Selected countries with their data (flag/name), sorted alphabetically.
      const set = this.selectedSet;
      return this.countries
        .filter((c) => set.has(c.iso))
        .sort((a, b) => (a.display_name || "").localeCompare(b.display_name || ""));
    },
    filteredSelectedCountries() {
      const q = this.selSearch.trim().toLowerCase();
      if (!q) return this.selectedCountries;
      return this.selectedCountries.filter(
        (c) =>
          (c.display_name || "").toLowerCase().includes(q) ||
          (c.iso || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    isSelected(iso) {
      return this.selectedSet.has(iso);
    },
    emit(next) {
      this.$emit("input", next);
    },
    toggleCountry(iso) {
      const next = new Set(this.value);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      this.emit([...next]);
    },
    countriesOfContinent(code) {
      return this.countries.filter((c) => c.continent === code).map((c) => c.iso);
    },
    selectedInContinent(code) {
      const set = this.selectedSet;
      return this.countriesOfContinent(code).filter((iso) => set.has(iso)).length;
    },
    isContinentFullySelected(code) {
      const isos = this.countriesOfContinent(code);
      return isos.length > 0 && isos.every((iso) => this.selectedSet.has(iso));
    },
    toggleContinent(code) {
      const isos = this.countriesOfContinent(code);
      const next = new Set(this.value);
      if (this.isContinentFullySelected(code)) {
        // Deselect the whole continent.
        isos.forEach((iso) => next.delete(iso));
      } else {
        // Select the whole continent.
        isos.forEach((iso) => next.add(iso));
      }
      this.emit([...next]);
    },
    continentTitle(cont) {
      return this.isContinentFullySelected(cont.code)
        ? this.$t("Deselect all") + " — " + this.$t(cont.label)
        : this.$t("Select all") + " — " + this.$t(cont.label);
    },
    clearAll() {
      this.emit([]);
    },
    openSelected() {
      if (!this.value.length) return;
      this.selSearch = "";
      this.showSelected = true;
    },
  },
};
</script>

<style scoped>
.cpk {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Toolbar */
.cpk__toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cpk__continents {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cpk__cont {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid #dcdee2;
  border-radius: 16px;
  background: #fff;
  color: #515a6e;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cpk__cont:hover {
  border-color: #2d8cf0;
  color: #2d8cf0;
}
.cpk__cont--full {
  background: #2d8cf0;
  border-color: #2d8cf0;
  color: #fff;
}
.cpk__cont-count {
  font-size: 11px;
  opacity: 0.85;
}

/* Summary */
.cpk__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cpk__selected-count {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  color: #19be6b;
  font-size: 13px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 6px;
}
.cpk__selected-count--clickable {
  cursor: pointer;
}
.cpk__selected-count--clickable:hover {
  background: #e8fbef;
}
.cpk__selected-eye {
  color: #808695;
  font-size: 14px;
}

/* Selected countries modal */
.cpk__sel-modal {
  max-height: 60vh;
  overflow-y: auto;
}
.cpk__sel-search {
  position: sticky;
  top: 0;
  z-index: 1;
  margin-bottom: 8px;
  background: #fff;
}
.cpk__sel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  transition: background 0.12s ease;
}
.cpk__sel-item:hover {
  background: #f7fbff;
}
.cpk__sel-name {
  flex: 1 1 auto;
  font-size: 13px;
  color: #1f2d3d;
}
.cpk__sel-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #b8bec9;
  cursor: pointer;
  border-radius: 6px;
  flex: 0 0 auto;
  transition: background 0.15s ease, color 0.15s ease;
}
.cpk__sel-remove:hover {
  background: #ffece8;
  color: #ed4014;
}
.cpk__clear {
  border: none;
  background: transparent;
  color: #808695;
  font-size: 13px;
  cursor: pointer;
}
.cpk__clear:hover {
  color: #ed4014;
}

/* Grid */
.cpk__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 6px;
  max-height: 46vh;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid #eef1f5;
  border-radius: 8px;
  background: #fafcff;
}
.cpk__country {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s ease;
}
.cpk__country:hover {
  background: #eef6ff;
}
.cpk__country--on {
  background: #e8f4ff;
}
.cpk__cb {
  cursor: pointer;
  flex: 0 0 auto;
}
.cpk__flag {
  width: 22px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
  flex: 0 0 auto;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}
.cpk__cname {
  font-size: 13px;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cpk__empty {
  grid-column: 1 / -1;
  text-align: center;
  color: #808695;
  padding: 24px;
  font-size: 13px;
}
</style>
